import React from 'react';
import { startOfWeek, differenceInCalendarDays, startOfDay, parseISO, isWithinInterval } from 'date-fns';
import { ApiService } from './ApiService';
import {
  HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse,
  HealthSystemDALEnumsLocationStableType,
  HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel
} from '../api/HealthSystemApi';

type Schedule = HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse;

const WEEK_STARTS_ON = 1 as const; // Monday

const findScheduleForDate = (schedules: Schedule[], date: Date): Schedule | null => {
  const target = startOfDay(date);
  return (
    schedules.find((s) => {
      const start = startOfDay(parseISO(s.startDate));
      const end = s.endDate ? parseISO(s.endDate) : null;
      if (!end) return target >= start;
      return isWithinInterval(target, { start, end });
    }) ?? null
  );
};

const computeWeekCycle = (schedule: Schedule, date: Date): number => {
  const anchor = startOfWeek(new Date(schedule.startDate), { weekStartsOn: WEEK_STARTS_ON });
  const daysDiff = differenceInCalendarDays(date, anchor);
  const totalWeekIndex = Math.floor(Math.max(0, daysDiff) / 7);
  return (totalWeekIndex % schedule.totalWeeks) + 1;
};

/**
 * Start of the working day (HH:mm) per task schedule id, for the given occurrences. The occurrence itself does not
 * carry it, so it is read from the task library of the week cycle each planned date falls in.
 */
export function useStartOfWorkingDays(
  occurrences: HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel[]
): Record<string, string | null | undefined> {
  const [allSchedules, setAllSchedules] = React.useState<Schedule[]>([]);
  const [startOfWorkingDays, setStartOfWorkingDays] = React.useState<Record<string, string | null | undefined>>({});

  React.useEffect(() => {
    new ApiService().api.taskList
      .taskListGetAllProductionSchedules()
      .then((result) => setAllSchedules(result.data))
      .catch(() => setAllSchedules([]));
  }, []);

  React.useEffect(() => {
    if (!occurrences.length || !allSchedules.length) return;

    const stablesByDate = new Map<string, Set<number>>();
    for (const occurrence of occurrences) {
      if (!stablesByDate.has(occurrence.plannedDate)) {
        stablesByDate.set(occurrence.plannedDate, new Set());
      }
      stablesByDate.get(occurrence.plannedDate)!.add(occurrence.stable as number);
    }

    const apiService = new ApiService();
    const promises: Promise<void>[] = [];
    const newMap: Record<string, string | null | undefined> = {};

    for (const [plannedDate, stables] of stablesByDate) {
      const date = parseISO(plannedDate);
      const schedule = findScheduleForDate(allSchedules, date);
      if (!schedule) continue;

      promises.push(
        apiService.api.taskList
          .taskListGetTaskLibraryList({
            Date: plannedDate,
            // HealthSystemDALEnumsLocationStableType is a numeric-literal union (0 | 10 | 20 | ...); `stables`
            // is built from `occurrence.stable as number`, so this can't be proven without a cast.
            Stables: Array.from(stables) as HealthSystemDALEnumsLocationStableType[],
            WeekCycle: computeWeekCycle(schedule, date)
          })
          .then((result) => {
            for (const [scheduleId, scheduleData] of Object.entries(result.data)) {
              newMap[scheduleId] = scheduleData.startOfWorkingDay;
            }
          })
          .catch(() => {})
      );
    }

    Promise.all(promises).then(() => setStartOfWorkingDays(newMap));
  }, [occurrences, allSchedules]);

  return startOfWorkingDays;
}
